import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, CheckSquare, Square, ArrowRight, PackageCheck } from "lucide-react";
import { formatMoney } from "@/lib/currency";

/**
 * Smart inbox — pieces at "Studio Editing" with ≥3 photos AND sale_price > 0.
 * One click bulk-advances them to "Listing & Marketing".
 */
export default function ReadyToPublishInbox() {
  const [data, setData] = useState({ count: 0, items: [], criteria: null });
  const [loading, setLoading] = useState(true);
  const [excluded, setExcluded] = useState(new Set());
  const [advancing, setAdvancing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/inventory/ready-to-publish");
      setData(d);
      setExcluded(new Set());
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("ready-to-publish load failed", e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedIds = data.items.filter((i) => !excluded.has(i.id)).map((i) => i.id);

  const advance = async () => {
    if (!selectedIds.length) {
      toast.error("nothing selected to advance.");
      return;
    }
    setAdvancing(true);
    try {
      const { data: r } = await api.post("/inventory/bulk-advance", {
        item_ids: selectedIds,
        target_stage: data.criteria?.target_stage || "Listing & Marketing",
      });
      toast.success(`${r.advanced} piece${r.advanced === 1 ? "" : "s"} advanced to ${r.target_stage}.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "bulk advance failed.");
    } finally {
      setAdvancing(false);
    }
  };

  if (loading) {
    return <p className="text-xs text-taupe">loading the publish queue…</p>;
  }

  return (
    <section data-testid="ready-to-publish-inbox">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-3xl lowercase text-charcoal flex items-center gap-3">
            <PackageCheck className="w-5 h-5 text-brass" /> ready to publish
          </h2>
          <p className="text-xs text-espresso mt-1">
            pieces at <span className="text-brass">studio editing</span> with at least {data.criteria?.min_photos || 3} photos and a sale price set.
          </p>
        </div>
        {data.count > 0 && (
          <button
            onClick={advance}
            disabled={advancing || !selectedIds.length}
            data-testid="bulk-advance-btn"
            className="bg-brass hover:bg-espresso text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="w-3 h-3" />
            advance {selectedIds.length} → listing &amp; marketing
            <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {data.count === 0 ? (
        <div className="border border-dashed border-taupe/50 p-8 text-center bg-linen/50" data-testid="empty-publish-queue">
          <p className="font-serif text-xl lowercase text-taupe">no pieces ready to publish right now.</p>
          <p className="text-xs text-espresso mt-2">photograph, edit, and price your &ldquo;studio editing&rdquo; stage pieces — they&apos;ll queue up here automatically.</p>
        </div>
      ) : (
        <div className="border border-taupe/40 bg-ivory">
          <ul className="divide-y divide-taupe/30" data-testid="publish-queue-list">
            {data.items.map((it) => {
              const isExcluded = excluded.has(it.id);
              return (
                <li
                  key={it.id}
                  className={`flex items-center gap-4 px-4 py-3 transition-colors ${isExcluded ? "opacity-50" : ""}`}
                  data-testid={`queue-item-${it.id.slice(0, 8)}`}
                >
                  <button
                    onClick={() => toggle(it.id)}
                    className="text-charcoal hover:text-brass"
                    data-testid={`queue-toggle-${it.id.slice(0, 8)}`}
                    aria-label={isExcluded ? "include" : "exclude"}
                  >
                    {isExcluded ? <Square className="w-5 h-5" /> : <CheckSquare className="w-5 h-5 text-brass" />}
                  </button>
                  <div className="w-14 h-14 bg-linen border border-taupe/30 overflow-hidden flex-shrink-0">
                    {it.photos?.[0] ? <img src={fileUrl(it.photos[0])} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/admin/inventory/${it.id}`} className="font-serif text-base lowercase text-charcoal hover:text-brass truncate block">
                      {it.title}
                    </Link>
                    <p className="text-[0.7rem] text-taupe tracking-widest uppercase mt-0.5">
                      {it.brand || "—"} · {it.type || "—"} · {it.photos?.length || 0} photo{it.photos?.length === 1 ? "" : "s"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-lg text-brass tabular-nums">{formatMoney(it.sale_price)}</p>
                    <p className="text-[0.6rem] text-taupe uppercase tracking-widest">sale price</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
